//Built-in libraries

import { ChevronLeftIcon, ChevronRightIcon, MapPinIcon } from '@heroicons/react/24/solid';
import { useQueries } from '@tanstack/react-query';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { useEffect, useMemo, useRef, useState } from 'react';
//Third party libraries
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { getAddressDecode } from '@/api/misc';
import type { IGetProjectsServiceResData } from '@/api/Sherpa/Project/modules/ProjectCore/types';
import GoogleMap from '@/components/UI/GoogleMap';
import { PageFooterPagination } from '@/components/UI/Pagination/PageFooterPagination';
import { useProjectsData } from '@/hooks/ReactQueryHooks/useProjectQuery';
//Services
import { usePermission } from '@/hooks/usePermission';
import { workspaceSelector } from '@/store';
import { notifyUser } from '@/store/Toast';
import type { FiltersInterface } from '@/types/Miscellaneous';
//Assets andd Miscellaneous
import type { ListProjectType } from '@/types/Project';
import type ProjectDetail from '@/types/ProjectwithGeo';
import { isObjectEmpty } from '@/utils';
import { toGeoCoordinate } from '@/utils/geoCoordinates';
//Custom components
import MapViewProjectCard from './MapViewProjectCard';

const DEFAULT_MAP_CENTER = { lat: 39.8283, lng: -98.5795 };

function buildProjectAddress(project: IGetProjectsServiceResData & { address?: string; city?: string; state?: string; zipCode?: string }) {
  if (project.fullAddress?.trim()) {
    return project.fullAddress.trim();
  }

  return [project.address, project.city, project.state, project.zipCode].filter(Boolean).join(', ').trim();
}

//Component Begin
export const MapViewProjectsList = ({ filters }: { filters: FiltersInterface }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();
  const { currentWorkspace } = useSelector(workspaceSelector);
  const [selectedProjectId, setSelectedProjectId] = useState(0);
  const [selectedMarker, setSelectedMarker] = useState<number>(0);
  const projectLocationHandler = (data: ProjectDetail) => {
    setSelectedProjectId(data.id);
  };

  const [page, setPage] = useState<number>(1);
  const fetchProjects = useProjectsData({
    params: { perPage: 12, page, ...filters },
  });
  const projects = fetchProjects?.data?.data;
  const totalProjects = fetchProjects?.data?.meta?.total;
  const totalPages = Math.ceil(Number(totalProjects) / 12);

  const onPageChangeHandler = (direction: string) => {
    if (direction === 'prev') {
      setPage((prevState) => (prevState > 1 ? prevState - 1 : 1));
    } else if (direction === 'next') {
      setPage((prevState) => (prevState >= totalPages ? totalPages : prevState + 1));
    }
  };

  const refs = useRef<Array<HTMLLIElement | null>>([]);

  useEffect(() => {
    const selectedProjectIndex = projects?.findIndex((project: ListProjectType) => project.id === selectedProjectId);
    if (selectedProjectIndex != null && selectedProjectIndex >= 0 && refs.current[selectedProjectIndex]) {
      refs.current[selectedProjectIndex]?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [selectedProjectId, projects]);

  const projectDetailViewHandler = (projectId: number) => {
    if (hasPermission(['READ_PROJECT'])) {
      navigate(`${location.pathname}/${projectId}`, { state: location });
    } else {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'You do not have permission to read project details',
          toastType: 'info',
        })
      );
    }
  };

  const fetchProjectGeoCode = useQueries({
    queries: (projects ?? []).map((project: IGetProjectsServiceResData) => {
      const address = buildProjectAddress(project);

      return {
        queryKey: ['GET_PROJECT_GEO_CODE', project.id, address],
        enabled: address.length >= 5,
        retry: 1,
        queryFn: async (): Promise<ProjectDetail | null> => {
          try {
            const response = await getAddressDecode(address);
            const coordinates = toGeoCoordinate(response?.data?.lat, response?.data?.lng);

            if (!coordinates) {
              return null;
            }

            return {
              ...project,
              coordinates,
            };
          } catch {
            return null;
          }
        },
      };
    }),
  });

  const geocodedProjects = useMemo(
    () => fetchProjectGeoCode.map((query) => query.data).filter((project): project is ProjectDetail => project != null),
    [fetchProjectGeoCode]
  );

  const mapCenter = geocodedProjects[0]?.coordinates ?? DEFAULT_MAP_CENTER;

  return (
    <>
      {!fetchProjects.isLoading && projects && totalProjects > 0 && (
        <>
          <div className='flex justify-between items-center my-5'>
            <p>{totalProjects} Projects</p>
            <div className='flex gap-2'>
              <div
                className={`rounded  cursor-pointer ${
                  page === 1 ? 'bg-neutralColor-light' : 'bg-primaryColor-normal hover:bg-primaryColor-medium'
                }`}
              >
                <ChevronLeftIcon onClick={() => onPageChangeHandler('prev')} className='text-white h-6 w-6 p-1' />
              </div>
              <div
                className={`bg-primaryColor-normal rounded hover:bg-primaryColor-medium cursor-pointer ${
                  page === totalPages ? 'bg-neutralColor-light' : 'bg-primaryColor-normal hover:bg-primaryColor-medium'
                }`}
              >
                <ChevronRightIcon onClick={() => onPageChangeHandler('next')} className='text-white h-6 w-6 p-1' />
              </div>
            </div>
          </div>

          <section>
            <div className='w-full lg:h-screen/70 flex flex-col xl:flex-row justify-between bg-white'>
              <div className='w-full h-screen/3 xl:h-full overflow-hidden'>
                <GoogleMap defaultCenter={mapCenter}>
                  {geocodedProjects.map((project) => (
                    <AdvancedMarker key={project.id} position={project.coordinates}>
                      <div
                        onClick={() => {
                          setSelectedMarker(project.id);
                          projectLocationHandler(project);
                        }}
                        className='relative'
                      >
                        <MapPinIcon className={selectedMarker === project.id ? 'h-8 w-8 text-black' : 'h-8 w-8 text-red-500 '} />
                      </div>
                    </AdvancedMarker>
                  ))}
                </GoogleMap>
              </div>
              <ul className='p-5 w-full h-full overflow-y-scroll sm:no-scrollbar xl:w-2/3 gap-4 flex flex-col 2xl:grid 2xl:grid-cols-1 items-top'>
                {projects?.map((project: IGetProjectsServiceResData, idx: number) => (
                  <div key={project.id} onClick={() => projectDetailViewHandler(project.id)}>
                    <MapViewProjectCard
                      ref={(element) => {
                        refs.current[idx] = element;
                      }}
                      project={project}
                      projectCount={projects.length}
                      selectedProjectId={selectedProjectId}
                    />
                  </div>
                ))}
              </ul>
            </div>
          </section>
          <PageFooterPagination totalPages={totalPages} page={page} setPage={setPage} />
        </>
      )}
      {!fetchProjects.isLoading && !isObjectEmpty(filters) && totalProjects === 0 && (
        <section>
          <div className='w-full flex justify-center items-center h-screen/2 bg-white p-4 text-center'>
            <div>
              <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                <MapPinIcon className='h-8 w-8' />
                <h1>{totalProjects} Projects</h1>
              </div>
              <p>
                There are {totalProjects} projects matching your criteria in{' '}
                {currentWorkspace ? <span className='text-primaryColor-normal'>{currentWorkspace?.name}</span> : 'all'} program.
              </p>
            </div>
          </div>
        </section>
      )}
    </>
  );
};
